import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import download from 'downloadjs'
import { useCallback, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { useSegment } from '../../../hooks'
import { fetchDocumentPreviewBlob } from '../query-api'
import type { DmsDownloadTrackingProperties } from '../types'

export const useDocumentDownload = () => {
  const [isDownloading, setIsDownloading] = useState(false)
  const { track } = useSegment()

  const downloadDocument = useCallback(
    async (organizationId: string, documentId: string, fileName: string, fileCount = 1) => {
      setIsDownloading(true)
      const workflowId = uuidv4()

      const trackingPayload: DmsDownloadTrackingProperties = {
        /* eslint-disable @typescript-eslint/naming-convention -- Segment event properties must match the analytics schema's snake_case names. */
        dms_download_type: 'document',
        dms_file_count: fileCount,
        workflow_id: workflowId,
        /* eslint-enable @typescript-eslint/naming-convention */
      }

      track('DMS Download Initiated', trackingPayload)

      try {
        const blob = await fetchDocumentPreviewBlob(organizationId, documentId, workflowId)
        download(blob, fileName)
        track('DMS Download Finished', trackingPayload)
      } catch (error: unknown) {
        trackError(error)
        notify({
          id: NotificationKeys.DocumentsDownload,
          message: error instanceof Error ? error.message : String(error),
          variant: 'error',
        })
      } finally {
        setIsDownloading(false)
      }
    },
    [track],
  )

  return { downloadDocument, isDownloading }
}
